/**
 * Validation utilities for form fields.
 * Each validator returns an error message string or '' if valid.
 */

// Ukrainian phone: +380XXXXXXXXX or 0XXXXXXXXX (10 digits after 0, or 12 after +380)
const PHONE_RE = /^(\+?3?8?)?(0\d{9})$/;

/**
 * Format phone as user types: +38 067 945 58 02
 * Accepts any input, strips non-digits, formats progressively.
 */
export function formatPhone(value) {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';

  // Normalize: 0XX... → 380XX..., 80XX... → 380XX...
  let d = digits;
  if (d.startsWith('380')) { /* already full */ }
  else if (d.startsWith('80') && d.length >= 3) d = '3' + d;
  else if (d.startsWith('0') && d.length >= 2) d = '38' + d;
  // If user typed just 1-2 digits that don't start with 3, show as-is with +
  // e.g. typing "0" → "+0", "06" → "+38 06", "3" → "+3"

  // Format: +38 0XX XXX XX XX  (max 12 digits = +380XXXXXXXXX)
  d = d.slice(0, 12);
  if (d.length <= 2) return '+' + d;
  let r = '+' + d.slice(0, 2);                         // +38
  if (d.length > 2) r += ' ' + d.slice(2, 5);          // 067
  if (d.length > 5) r += ' ' + d.slice(5, 8);          // 945
  if (d.length > 8) r += ' ' + d.slice(8, 10);         // 58
  if (d.length > 10) r += ' ' + d.slice(10, 12);       // 02

  return r;
}

export function validatePhone(phone) {
  if (!phone || !phone.trim()) return '';  // not required by default
  const digits = phone.replace(/[\s\-()]/g, '');
  if (!PHONE_RE.test(digits)) return 'Невірний формат телефону';
  return '';
}

export function validatePhoneRequired(phone) {
  if (!phone || !phone.trim()) return 'Телефон обов\'язковий';
  return validatePhone(phone);
}

// Email
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(email) {
  if (!email || !email.trim()) return '';  // not required by default
  if (!EMAIL_RE.test(email.trim())) return 'Невірний формат email';
  return '';
}

// Required text field
export function validateRequired(value, fieldName = 'Поле') {
  if (!value || !String(value).trim()) return `${fieldName} обов'язкове`;
  return '';
}

// Numeric range
export function validateRange(value, min, max, fieldName = 'Значення') {
  const n = Number(value);
  if (isNaN(n)) return `${fieldName}: введіть число`;
  if (min !== undefined && n < min) return `${fieldName}: мінімум ${min}`;
  if (max !== undefined && n > max) return `${fieldName}: максимум ${max}`;
  return '';
}

// Time: start must be before end
export function validateTimeRange(start, end) {
  if (!start || !end) return '';
  if (start >= end) return 'Час початку має бути раніше за час кінця';
  return '';
}

// Date not in past (for appointments)
export function validateDateNotPast(dateStr) {
  if (!dateStr) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(dateStr + 'T00:00:00');
  if (date < today) return 'Дата не може бути в минулому';
  return '';
}

// Collect errors: takes { fieldName: errorMsg } object, returns first error or ''
export function firstError(errors) {
  return Object.values(errors).find(e => e) || '';
}

// Validate all and return errors object
export function validateAll(validators) {
  const errors = {};
  for (const [key, fn] of Object.entries(validators)) {
    errors[key] = fn();
  }
  return errors;
}

// Check if errors object has any errors
export function hasErrors(errors) {
  return Object.values(errors).some(e => e);
}

// Safely extract string from Meest API fields that may be objects like { descrUA, descrlo }
export function meestStr(v) {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.descrUA || v.descr || v.descrlo || Object.values(v)[0] || '';
  return String(v);
}
